import { Matrix4 } from 'three'
import { recordGeometryRejections, rejectionFromValidation } from './debug'
import { currentGeometryOperation } from './operation'
import { findAllUnitCollisions, validateUnitPose } from './validate'
import { isOccupyingUnit, type UnitGeometry } from './unitObb'

type GeomSnap = {
  elements: number[] | null
  x: number
  y: number
  z: number
  rotation: number | undefined
  width: number
  height: number
  thickness: number
  snappedSides: unknown
  signature: string
}

type IntegrityDraft = {
  items?: Map<string, UnitGeometry>
  layers?: Array<{ items?: Set<string>; selected?: Set<string> }>
  currentLayer?: number
  collisionableItems?: Set<string>
  sceneBoundingBoxes?: Map<string, unknown>
  currentObjectID?: string
  contadorModificaciones?: number
}

let persistedImportDepth = 0

/** La carga de un proyecto persistido informa colisiones y no reescribe la geometría. */
export function runAsPersistedGeometryImport<T>(fn: () => T): T {
  persistedImportDepth += 1
  try {
    return fn()
  } finally {
    persistedImportDepth -= 1
  }
}

export function isPersistedGeometryImport(): boolean {
  return persistedImportDepth > 0
}

function signatureOf(item: UnitGeometry | undefined): string {
  if (!item || !isOccupyingUnit(item)) return 'non-occupying'
  const elements = item.matrix ? Array.from(item.matrix.elements).join(',') : ''
  return [
    item.width,
    item.height,
    item.thickness,
    item.x,
    item.y,
    item.z,
    item.rotation,
    item.visible,
    item.canCollide,
    elements,
  ].join('|')
}

function captureGeometry(items: Map<string, UnitGeometry> | undefined): Map<string, GeomSnap> {
  const snap = new Map<string, GeomSnap>()
  if (!items) return snap
  items.forEach((item, id) => {
    if (!item || typeof item !== 'object') return
    snap.set(String(id), {
      elements: item.matrix ? Array.from(item.matrix.elements) : null,
      x: Number(item.x) || 0,
      y: Number(item.y) || 0,
      z: Number(item.z) || 0,
      rotation: item.rotation,
      width: Number(item.width) || 0,
      height: Number(item.height) || 0,
      thickness: Number(item.thickness) || 0,
      snappedSides: item.snappedSides ? { ...(item.snappedSides as object) } : item.snappedSides,
      signature: signatureOf(item),
    })
  })
  return snap
}

function restoreSnap(item: UnitGeometry, snap: GeomSnap) {
  item.x = snap.x
  item.y = snap.y
  item.z = snap.z
  item.rotation = snap.rotation
  item.width = snap.width
  item.height = snap.height
  item.thickness = snap.thickness
  item.snappedSides = snap.snappedSides ? { ...(snap.snappedSides as object) } : snap.snappedSides
  if (snap.elements) {
    // Nueva referencia: React y Three.js vuelven a copiar la pose válida.
    item.matrix = new Matrix4().fromArray(snap.elements)
  }
}

function purgeNewUnit(draft: IntegrityDraft, id: string) {
  draft.items?.delete(id)
  if (Array.isArray(draft.layers)) {
    for (const layer of draft.layers) {
      layer?.items?.delete(id)
      layer?.selected?.delete(id)
    }
  }
  draft.collisionableItems?.delete(id)
  draft.sceneBoundingBoxes?.delete(id)
  if (String(draft.currentObjectID ?? '') === id) draft.currentObjectID = ''
}

/**
 * Revierte, dentro del mismo draft, las poses que intersectan.
 * La pose inválida no llega al estado confirmado.
 * Usa las unidades del draft después de la receta: es el estado que se va a commitear.
 */
export function enforceDraftGeometryIntegrity(draft: IntegrityDraft, before: Map<string, GeomSnap>) {
  const items = draft.items
  if (!items) return []

  const rejections = []
  const rejectedIds = new Set<string>()
  const purgeIds: string[] = []

  items.forEach((item, key) => {
    const mapKey = String(key)
    const id = String(item?.id ?? key)
    if (!isOccupyingUnit(item)) return
    const previous = before.get(mapKey)
    if (previous && previous.signature === signatureOf(item)) return

    const validation = validateUnitPose(item, undefined, items, id)
    if (validation.valid) return

    rejections.push(rejectionFromValidation(id, validation, item.snappedSides))
    rejectedIds.add(id)
    rejectedIds.add(mapKey)

    if (!previous) {
      purgeIds.push(mapKey)
      return
    }
    restoreSnap(item, previous)
  })

  for (const id of purgeIds) purgeNewUnit(draft, id)

  if (rejectedIds.size > 0) {
    items.forEach((item, key) => {
      const id = String(key)
      const previous = before.get(id)
      if (!previous || !item) return
      const serialized = JSON.stringify(item.snappedSides ?? null)
      const mentionsRejected = Array.from(rejectedIds).some((rejectedId) => serialized.includes(rejectedId))
      if (rejectedIds.has(id) || mentionsRejected) {
        item.snappedSides = previous.snappedSides
          ? { ...(previous.snappedSides as object) }
          : previous.snappedSides
      }
    })
    draft.contadorModificaciones = (draft.contadorModificaciones || 0) + 1
  }

  return rejections
}

/**
 * Única frontera de las escrituras que pasan por set() de Zustand.
 * La validación ocurre dentro de la receta, antes de que immer publique el estado.
 */
export function applyGuardedUpdate(
  set: (partial: unknown, replace?: boolean) => void,
  partial: unknown,
  replace?: boolean,
) {
  if (isPersistedGeometryImport()) {
    set(partial, replace)
    return
  }

  if (typeof partial === 'function') {
    const recipe = partial as (draft: IntegrityDraft) => unknown
    set((draft: IntegrityDraft & Record<string, unknown>) => {
      const before = captureGeometry(draft.items)
      const returned = recipe(draft)
      if (returned && typeof returned === 'object') {
        for (const [key, value] of Object.entries(returned as Record<string, unknown>)) {
          draft[key] = value
        }
      }
      const rejections = enforceDraftGeometryIntegrity(draft, before)
      if (rejections.length > 0) recordGeometryRejections(rejections)
    }, replace)
    return
  }

  if (replace && partial && typeof partial === 'object' && 'items' in (partial as object)) {
    const items = (partial as { items?: Map<string, UnitGeometry> }).items
    if (items) {
      const collisions = findAllUnitCollisions(items)
      if (collisions.length > 0) {
        recordGeometryRejections(
          collisions.map((pair) => ({
            timestamp: Date.now(),
            operation: currentGeometryOperation(),
            unitId: pair.unitA,
            reason: 'GEOMETRIC_COLLISION' as const,
            candidate: pair.unitADump,
            collidingUnitIds: [pair.unitB],
            collidingUnits: [pair.unitBDump],
            snappedSides: pair.unitADump.snappedSides,
          })),
        )
        return
      }
    }
  }

  set(partial, replace)
}
